import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Chip,
    Button,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Modal from '@mui/material/Modal';
import CloseIcon from '@mui/icons-material/Close';
import { buildApiUrl } from '../config/api';

function ResultRow({ row, onView }) {
    // Mostra apenas o SUBJID (participant_nickname)
    const participant = row.participant_nickname || '-';
    
    const getValidationChip = () => {
        const status = row.validation || 'NOT_REVIEWED';
        let color = 'default';
        let label = 'Não Revisado';
        
        if (status === 'ACCEPTED') {
            color = 'success';
            label = 'Aceito';
        } else if (status === 'REJECTED') {
            color = 'error';
            label = 'Rejeitado';
        }
        
        return <Chip size="small" label={label} color={color} />;
    };
    
    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>{participant}</TableCell>
                <TableCell>{row.envelope_id}</TableCell>
                <TableCell>{format(new Date(row.timestamp), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}</TableCell>
                <TableCell align="center">{getValidationChip()}</TableCell>
                <TableCell align="center">
                    <Button variant="outlined" size="small" onClick={() => onView(row)}>Visualizar</Button>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}

function ResultModal({ open, onClose, row, onValidationUpdate }) {
    const { token } = useAuth();
    const [sentence, setSentence] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [validation, setValidation] = useState('NOT_REVIEWED');
    const [validationComment, setValidationComment] = useState('');
    const [saving, setSaving] = useState(false);
    const [showPartialConfirmDialog, setShowPartialConfirmDialog] = useState(false);

    useEffect(() => {
        async function fetchSentence() {
            if (row && row.envelope_id) {
                setLoading(true);
                setError('');
                try {
                    const resp = await fetch(buildApiUrl(`admin/sentence/${row.envelope_id}`), {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (!resp.ok) throw new Error('Erro ao buscar sentença');
                    const data = await resp.json();
                    setSentence(data.paragraph);
                } catch (e) {
                    setError('Erro ao buscar sentença');
                    setSentence(null);
                } finally {
                    setLoading(false);
                }
            } else {
                setSentence(null);
            }
        }
        if (open && row) {
            fetchSentence();
            // Set initial validation values from row data
            setValidation(row.validation || 'NOT_REVIEWED');
            setValidationComment(row.validation_comment || '');
        }
    }, [row, open, token]);

    const saveValidation = async (markAsComplete = false) => {
        setSaving(true);
        try {
            const response = await fetch(buildApiUrl(`admin/submissions/${row.id}/validate`), {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    validation: validation,
                    validation_comment: validationComment,
                    mark_as_complete: markAsComplete
                })
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `Erro ${response.status}`);
            }
            // Update parent component if callback provided
            if (onValidationUpdate) {
                onValidationUpdate(row.id, validation, validationComment, markAsComplete);
            }
            // Close modal on success
            onClose();
        } catch (e) {
            alert('Erro ao salvar validação: ' + e.message);
        } finally {
            setSaving(false);
            setShowPartialConfirmDialog(false);
        }
    };

    const handleSaveValidation = async () => {
        // Check if this is a partial session (not confirmed)
        if (!row.confirmed) {
            // Show confirmation dialog for partial sessions
            setShowPartialConfirmDialog(true);
        } else {
            // Session is already complete, save directly
            await saveValidation(false);
        }
    };

    const handleConfirmPartialValidation = async () => {
        await saveValidation(true);
    };

    const handleCancelPartialValidation = () => {
        setShowPartialConfirmDialog(false);
    };

    if (!row) return null;
    const envelopeImageUrl = row.envelope_photo_path ? buildApiUrl(`images/${row.envelope_photo_path.split('/').pop()}`) : null;
    const responseImageUrl = row.response_photo_path ? buildApiUrl(`images/${row.response_photo_path.split('/').pop()}`) : null;
    const handleImageClick = (url) => {
        if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };
    return (
    <>
        <Modal open={open} onClose={onClose}>
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', boxShadow: 24, p: 4, minWidth: 600, maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Detalhes da Coleta</Typography>
                    <IconButton onClick={onClose}><CloseIcon /></IconButton>
                </Box>
                <Typography variant="body2" sx={{ mb: 0 }}>
                    {row.participant_nickname || '-'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    ID da Sentença: {row.envelope_id}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1">Texto Coletado</Typography>
                        <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 1, minHeight: 80, maxHeight: 200, overflowY: 'auto', fontFamily: 'monospace', whiteSpace: 'pre-wrap', background: '#fafafa', fontSize: 15, lineHeight: 1.5, boxSizing: 'border-box', display: 'block' }}>
                            {row.response_text || 'N/A'}
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1">Sentença</Typography>
                        <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 1, minHeight: 80, maxHeight: 200, overflowY: 'auto', fontFamily: 'monospace', whiteSpace: 'pre-wrap', background: '#fafafa', fontSize: 15, lineHeight: 1.5, boxSizing: 'border-box', display: 'block' }}>
                            {loading ? 'Carregando...' : (sentence || error || 'N/A')}
                        </Box>
                    </Box>
                </Box>
                <Typography variant="subtitle1" gutterBottom>Imagens:</Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption">Foto do QR code</Typography><br/>
                        {envelopeImageUrl ? (
                            <img
                                src={envelopeImageUrl}
                                alt="Envelope"
                                style={{ maxHeight: '150px', maxWidth: '100%', cursor: 'pointer' }}
                                onClick={() => handleImageClick(envelopeImageUrl)}
                            />
                        ) : 'N/A'}
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption">Resposta</Typography><br/>
                        {responseImageUrl ? (
                            <img
                                src={responseImageUrl}
                                alt="Resposta"
                                style={{ maxHeight: '150px', maxWidth: '100%', cursor: 'pointer' }}
                                onClick={() => handleImageClick(responseImageUrl)}
                            />
                        ) : 'N/A'}
                    </Box>
                </Box>
                <Typography variant="subtitle1" gutterBottom>Data/Hora:</Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>{row.timestamp ? format(new Date(row.timestamp), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR }) : '-'}</Typography>
                <Typography variant="subtitle1" gutterBottom>Datas de Texto:</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Criado: {row.text_created_at ? format(new Date(row.text_created_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR }) : '-'}<br/>
                    Editado: {row.text_edited_at ? format(new Date(row.text_edited_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR }) : '-'}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>Datas de Fotos:</Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Foto QR code Criada: {row.envelope_photo_created_at ? format(new Date(row.envelope_photo_created_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR }) : '-'}<br/>
                    Foto QR code Editada: {row.envelope_photo_edited_at ? format(new Date(row.envelope_photo_edited_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR }) : '-'}<br/>
                    Foto Resposta Criada: {row.response_photo_created_at ? format(new Date(row.response_photo_created_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR }) : '-'}<br/>
                    Foto Resposta Editada: {row.response_photo_edited_at ? format(new Date(row.response_photo_edited_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR }) : '-'}
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <Chip
                        label={row.confirmed ? 'completo' : 'parcial'}
                        color={row.confirmed ? 'success' : 'default'}
                        size="small"
                        sx={{
                            bgcolor: row.confirmed ? 'success.main' : 'grey.500',
                            color: row.confirmed ? 'white' : 'white',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                        }}
                    />
                </Box>
                <Box sx={{ mt: 3, borderTop: '1px solid #ddd', pt: 2 }}>
                    <Typography variant="h6" gutterBottom>Validação</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status de Validação</InputLabel>
                            <Select
                                value={validation}
                                label="Status de Validação"
                                onChange={(e) => setValidation(e.target.value)}
                            >
                                <MenuItem value="NOT_REVIEWED">Não Revisado</MenuItem>
                                <MenuItem value="ACCEPTED">Aceito</MenuItem>
                                <MenuItem value="REJECTED">Rejeitado</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Comentário de Validação"
                            multiline
                            rows={3}
                            value={validationComment}
                            onChange={(e) => setValidationComment(e.target.value)}
                            size="small"
                        />
                        <Button
                            variant="contained"
                            onClick={handleSaveValidation}
                            disabled={saving}
                            sx={{ alignSelf: 'flex-start' }}
                        >
                            {saving ? 'Salvando...' : 'Salvar Validação'}
                        </Button>
                        {row.validation_timestamp && (
                            <Typography variant="caption" color="textSecondary">
                                Última validação: {format(new Date(row.validation_timestamp), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        </Modal>

        {/* Confirmation dialog for partial sessions */}
        <Dialog
            open={showPartialConfirmDialog}
            onClose={handleCancelPartialValidation}
            aria-labelledby="partial-session-dialog-title"
            aria-describedby="partial-session-dialog-description"
        >
            <DialogTitle id="partial-session-dialog-title">
                Sessão Parcial Detectada
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="partial-session-dialog-description">
                    Esta sessão está marcada como <strong>PARCIAL</strong>, o que significa que o participante
                    não clicou no botão final para confirmar a conclusão.
                    <br /><br />
                    Ao salvar a validação, esta sessão será automaticamente marcada como <strong>COMPLETA</strong> e irá impedir novos ajustes do usuário.
                    <br /><br />
                    Deseja continuar?
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancelPartialValidation} color="inherit">
                    Cancelar
                </Button>
                <Button onClick={handleConfirmPartialValidation} variant="contained" color="primary" autoFocus disabled={saving}>
                    {saving ? 'Salvando...' : 'Confirmar e Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    </>
    );
}

function ExportAllButton({ token }) {
    return (
        <Button
            variant="contained"
            color="primary"
            sx={{ mb: 2 }}
            onClick={async () => {
                try {
                    const response = await fetch(buildApiUrl('admin/export/all'), {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (!response.ok) {
                        let errorMsg = `Erro ${response.status}`;
                        try {
                            const errorData = await response.json();
                            errorMsg = errorData.detail || errorMsg;
                        } catch (e) { /* Ignore */ }
                        throw new Error(errorMsg);
                    }
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'dados_coleta.zip';
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                } catch (err) {
                    alert('Erro ao exportar: ' + (err.message || err));
                }
            }}
        >
            Exportar todos os dados (ZIP)
        </Button>
    );
}

function AdminResults() {
    const { token } = useAuth();
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        const fetchResults = async () => {
            setIsLoading(true);
            setError('');
            try {
                const response = await fetch(buildApiUrl(`admin/results?page=${page}&size=100`), {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });
                if (!response.ok) {
                    let errorMsg = `Erro ${response.status}`;
                    try {
                        const errorData = await response.json();
                        errorMsg = errorData.detail || errorMsg;
                    } catch (e) { /* Ignore */ }
                    throw new Error(errorMsg);
                }
                const data = await response.json();
                setResults(data.results || []);
                setTotalPages(data.pages || 1);
            } catch (err) {
                setError(err.message || 'Ocorreu um erro ao buscar os resultados.');
                setResults([]);
            } finally {
                setIsLoading(false);
            }
        };
        if (token) {
            fetchResults();
        }
    }, [token, page]);

    return (
        <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <ExportAllButton token={token} />
            </Box>
            <Typography variant="h5" gutterBottom>
                Visualizar Resultados das Coletas
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table aria-label="results table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Participante</TableCell>
                                <TableCell>Envelope ID</TableCell>
                                <TableCell>Data/Hora</TableCell>
                                <TableCell align="center">Validação</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {results.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">Nenhum resultado encontrado.</TableCell>
                                </TableRow>
                            ) : (
                                results.map((row) => (
                                    <ResultRow key={row.id} row={row} onView={r => { setSelectedRow(r); setModalOpen(true); }} />
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <ResultModal 
                open={modalOpen} 
                onClose={() => setModalOpen(false)} 
                row={selectedRow}
                onValidationUpdate={(id, validation, comment, markedAsComplete) => {
                    // Update the local results to reflect the validation change
                    setResults(prevResults =>
                        prevResults.map(r =>
                            r.id === id
                                ? {
                                    ...r,
                                    validation,
                                    validation_comment: comment,
                                    // Update confirmed status if marked as complete
                                    confirmed: markedAsComplete ? true : r.confirmed
                                }
                                : r
                        )
                    );
                }}
            />
            {/* Paginação simples */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                <Typography sx={{ mx: 2 }}>Página {page} de {totalPages}</Typography>
                <Button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Próxima</Button>
            </Box>
        </Box>
    );
}

export default AdminResults;

